import type { Tone } from '@leanin/ui';
import type { ResourceKind } from '@leanin/shared/resource-kinds';
import { useTranslations } from '@/i18n/translations';

export type { ResourceKind };

type ResourcesTranslate = ReturnType<typeof useTranslations<'Resources'>>;

// Localized singular kind label for member surfaces (cards, detail). Admin
// views keep the English `KIND_LABELS` / `KIND_CARD_LABEL` maps below — those
// are deliberately not routed through the translator.
export function resourceKindLabel(
  kind: ResourceKind,
  t: ResourcesTranslate,
): string {
  return t(`kinds.${kind}`);
}

// Canonical resource-kind labels used across the article header, author
// form, and list cards. `KIND_CARD_LABEL` is a card-copy override seam — it
// currently matches `KIND_LABELS` exactly, but is kept separate so card
// wording can diverge from the canonical labels without touching every
// surface.
export const KIND_LABELS: Record<ResourceKind, string> = {
  article: 'Article',
  video: 'Video',
  toolkit: 'Toolkit',
  workshop: 'Workshop',
  essay: 'Essay',
  discussion_guide: 'Discussion guide',
  research: 'Research',
  podcast: 'Podcast',
  activity: 'Activity',
  playlist: 'Playlist',
};

export const KIND_CARD_LABEL: Record<ResourceKind, string> = {
  article: 'Article',
  video: 'Video',
  toolkit: 'Toolkit',
  workshop: 'Workshop',
  essay: 'Essay',
  discussion_guide: 'Discussion guide',
  research: 'Research',
  podcast: 'Podcast',
  activity: 'Activity',
  playlist: 'Playlist',
};

// Crop aspect for resource covers. Matches the dominant display surface:
// ResourceCard's MediaHeader renders covers at height 132 in a ~350px grid
// column (≈8:3, center/cover). The detail page's full-bleed hero has no
// fixed aspect, so the card anchors it.
export const RESOURCE_COVER_ASPECT = 8 / 3;

export type ResourceCardData = {
  id: string;
  kind: ResourceKind;
  title: string;
  lede: string | null;
  coverUrl: string | null;
  tag: string;
  tone: Tone;
  featured: boolean;
  saved: boolean;
};

// Tones reachable by `ResourceCard` / `MediaHeader`. Picked so that no card
// ever falls into `neutral`/`ink` (those are reserved for chrome).
const TONE_CYCLE: Tone[] = ['yellow', 'navy', 'emerald', 'burgundy', 'orange'];

function deriveTone(id: string): Tone {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) | 0;
  return TONE_CYCLE[Math.abs(h) % TONE_CYCLE.length]!;
}

export function adaptResourceSummary(s: {
  id: string;
  title: string;
  kind: ResourceKind;
  lede: string | null;
  coverImageUrl?: string | null;
  featuredAt: string | null;
  tags: ReadonlyArray<{ slug: string; label: string }>;
  saved?: boolean;
  [key: string]: unknown;
}): ResourceCardData {
  return {
    id: s.id,
    title: s.title,
    kind: s.kind,
    lede: s.lede,
    coverUrl: s.coverImageUrl ?? null,
    tag: s.tags[0]?.label ?? 'General',
    tone: deriveTone(s.id),
    featured: s.featuredAt !== null,
    saved: s.saved ?? false,
  };
}
